import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SFIcon } from '@/components/ui/sf-icon';
import { SkeletonCard } from '@/components/ui/skeleton';
import { useProfileData } from '@/hooks/useProfileData';
import { clearUserPII } from '@/lib/appUser';
import { signOut } from '@/lib/rpc/auth/session';
import { sfIcons } from '@/lib/sf-icons';
import { cn } from '@/lib/utils';
import { semanticTokens } from '@packages/config/theme';
import { Page, PageHeader, PageContent } from '@supabank/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import { AccountDetailsCard } from './AccountDetailsCard';
import { DashboardContextType } from './DashboardContext';
import { NotificationSettings } from './NotificationSettings';
import { UnifiedVerificationStatusCard } from './UnifiedVerificationStatusCard';

export function DashboardSettings() {
  const { t } = useTranslation('common');
  const profileData = useProfileData();
  const location = useLocation();
  const navigate = useNavigate();
  const context = useOutletContext<DashboardContextType>();
  const [isSigningOut, setIsSigningOut] = useState(false);

  // Determine active tab from URL
  // Default to 'profile' if on /dashboard/settings (no sub-route)
  const activeTab = location.pathname.includes('/notifications') ? 'notifications' : 'profile';

  const handleLogout = async () => {
    setIsSigningOut(true);
    try {
      await signOut();
      // Clear sensitive data from browser storage
      clearUserPII();
      // Redirect to home page
      window.location.href = '/';
    } catch (error) {
      console.error('[Settings] Logout failed:', error);
      setIsSigningOut(false);
      // Still redirect even on error to ensure user is logged out
      window.location.href = '/';
    }
  };

  if (profileData.loading) {
    return (
      <Page data-ux-contract="canon">
        <PageHeader
          icon={() => <SFIcon icon={sfIcons.settings} />}
          title="Settings"
          subtitle="Manage your account settings and preferences."
        />
        <PageContent>
          <div className="space-y-6">
            <SkeletonCard lines={4} showAvatar />
            <SkeletonCard lines={3} />
          </div>
        </PageContent>
      </Page>
    );
  }

  // Use a dummy user ID for now - in a real app this would come from auth
  const userId = 'user-123';

  return (
    <Page data-ux-contract="canon">
      <PageHeader
        icon={() => <SFIcon icon={sfIcons.settings} />}
        title="Settings"
        subtitle="Manage your account settings and preferences."
      />

      <PageContent>
        <div className="space-y-6">
          {/* Tab Navigation */}
          <div className={cn("flex space-x-1 rounded-lg p-1 w-fit", semanticTokens.bg.muted, semanticTokens.spacing.sm.replace('p-', 'p-'), semanticTokens.radius.lg)}>
            <Button
              variant={activeTab === 'profile' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => navigate('/dashboard/settings/profile')}
              className={cn("flex items-center gap-2 min-h-[44px]", semanticTokens.motion.standard, semanticTokens.focus.ring)}
            >
              <SFIcon icon={sfIcons.user} className="h-4 w-4" />
              Profile
            </Button>
            <Button
              variant={activeTab === 'notifications' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => navigate('/dashboard/settings/notifications')}
              className={cn("flex items-center gap-2 min-h-[44px]", semanticTokens.motion.standard, semanticTokens.focus.ring)}
            >
              <SFIcon icon={sfIcons.notifications} className="h-4 w-4" />
              Notifications
            </Button>
          </div>

          {/* Tab Content */}
          {activeTab === 'profile' && (
            <div className="space-y-6 animate-in fade-in-50 duration-240">
              <Card className={semanticTokens.motion.standard}>
                <CardHeader>
                  <CardTitle className={cn(semanticTokens.typography.h3, "flex items-center gap-2")}>
                    <SFIcon icon={sfIcons.user} className="h-5 w-5" />
                    Personal Information
                  </CardTitle>
                  <CardDescription>
                    Update your personal details and contact information.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Display Name - only show if exists */}
                  {profileData.fullName && (
                    <div className="space-y-2">
                      <Label htmlFor="display-name">Display Name</Label>
                      <Input
                        id="display-name"
                        value={profileData.fullName}
                        placeholder="Enter your display name"
                        disabled
                        className="min-h-[44px]"
                      />
                      <p className={cn(semanticTokens.typography.caption, semanticTokens.fg.muted)}>
                        Contact support to change your display name.
                      </p>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address</Label>
                    <div className="flex items-center gap-2">
                      <SFIcon icon={sfIcons.mail} className={cn("h-4 w-4", semanticTokens.fg.muted)} />
                      <Input
                        id="email"
                        type="email"
                        value={profileData.email || ''}
                        disabled
                        className="flex-1 min-h-[44px]"
                      />
                    </div>
                    <p className={cn(semanticTokens.typography.caption, semanticTokens.fg.muted)}>
                      Contact support to change your email address.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Verification Status - moved from Profile page */}
              <UnifiedVerificationStatusCard
                bridgeCustomerId={context.bridgeCustomerId}
                kycStatus={context.kycStatus}
                tosStatus={context.tosStatus}
                realtimeState={context.kycRealtime}
                onRefresh={context.onStatusRefresh}
              />

              {/* Account Details - moved from Profile page */}
              <AccountDetailsCard
                accountType="individual"
                accountStatus="active"
                accountCreatedAt={undefined}
                lastLoginAt={undefined}
                accountId={context.customerId || undefined}
                email={profileData.email || undefined}
              />

              <Card className={semanticTokens.motion.standard}>
                <CardHeader>
                  <CardTitle className={cn(semanticTokens.typography.h3, "flex items-center gap-2")}>
                    <SFIcon icon={sfIcons.settings} className="h-5 w-5" />
                    Preferences
                  </CardTitle>
                  <CardDescription>
                    Configure your language and regional settings.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="language">Language</Label>
                    <Select value="en" disabled>
                      <SelectTrigger className="min-h-[44px]">
                        <SelectValue placeholder="Select language" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="en">English</SelectItem>
                        <SelectItem value="ru">Русский</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className={cn(semanticTokens.typography.caption, semanticTokens.fg.muted)}>
                      Contact support to change your language preference.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {activeTab === 'notifications' && (
            <div className="animate-in fade-in-50 duration-240">
              <NotificationSettings userId={userId} isAdmin={false} />
            </div>
          )}

          {/* Payment Methods Info - at the bottom of the page */}
          <div className="pt-6 border-t">
            <Card className={cn(semanticTokens.motion.standard)}>
              <CardContent className="pt-6">
                <div className="flex flex-col gap-6">
                  {/* Payment Methods */}
                  <div className="space-y-3">
                    <div>
                      <h3 className={cn(semanticTokens.typography.h4, "mb-2")}>
                        Supported Payment Methods
                      </h3>
                      <p className={cn(semanticTokens.typography.caption, semanticTokens.fg.muted, "mb-4")}>
                        Fast and secure payment rails for global transactions
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 sm:gap-x-6">
                      <Badge variant="default" className={cn("px-3 py-1.5", semanticTokens.typography.caption)}>
                        US ACH
                      </Badge>
                      <Badge variant="default" className={cn("px-3 py-1.5", semanticTokens.typography.caption)}>
                        Euro IBAN
                      </Badge>
                      <Badge variant="default" className={cn("px-3 py-1.5", semanticTokens.typography.caption)}>
                        Mexico SPEI
                      </Badge>
                      <Badge variant="default" className={cn("px-3 py-1.5", semanticTokens.typography.caption)}>
                        Brazil PIX
                      </Badge>
                    </div>
                  </div>

                  {/* Statistics */}
                  <div className="flex items-center justify-center gap-x-4 gap-y-2 sm:gap-x-6 pt-2">
                    <div className={cn("flex items-center gap-2", semanticTokens.typography.caption, semanticTokens.fg.muted)}>
                      <SFIcon icon={sfIcons.clock} className="h-4 w-4" />
                      <span>15 min settlement time</span>
                    </div>
                    <div className={cn("h-4 w-px", semanticTokens.bg.muted)} aria-hidden="true" />
                    <div className={cn("flex items-center gap-2", semanticTokens.typography.caption, semanticTokens.fg.muted)}>
                      <SFIcon icon={sfIcons.globe} className="h-4 w-4" />
                      <span>180+ countries supported</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Logout Button - at the bottom of the page */}
          <div className="pt-6 border-t">
            <Card className={cn(semanticTokens.motion.standard, "border-destructive/20")}>
              <CardContent className="pt-6">
                <div className="flex flex-col gap-4">
                  <div>
                    <h3 className={cn(semanticTokens.typography.h4, "mb-1")}>
                      Sign Out
                    </h3>
                    <p className={cn(semanticTokens.typography.caption, semanticTokens.fg.muted)}>
                      Sign out of your account. You'll need to sign in again to access your dashboard.
                    </p>
                  </div>
                  <Button
                    variant="destructive"
                    onClick={handleLogout}
                    disabled={isSigningOut}
                    className={cn(
                      "w-full sm:w-auto min-h-[44px]",
                      semanticTokens.motion.standard,
                      semanticTokens.focus.ring
                    )}
                  >
                    <SFIcon
                      icon={isSigningOut ? sfIcons.loader : sfIcons.logout}
                      className={cn("h-4 w-4", isSigningOut && "animate-spin")}
                    />
                    {isSigningOut ? 'Signing out...' : 'Sign Out'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </PageContent>
    </Page>
  );
}
